import { useState } from 'react';
import { useLeaderboard } from '@/hooks/use-leaderboard';
import { useFitnessLeaderboard } from '@/hooks/use-fitness-results';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Trophy, Activity } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FITNESS_TEST_META, STANDARD_TEST_TYPES } from '@/shared/fitnessTestMeta';

const RANK_STYLES: Record<number, string> = {
  1: 'bg-yellow-400 text-yellow-900',
  2: 'bg-slate-300 text-slate-700',
  3: 'bg-amber-600 text-amber-100',
};

export default function CoachLeaderboardsPage() {
  const { data: rows, isLoading } = useLeaderboard();
  const [selectedTestType, setSelectedTestType] = useState<string>('beep_test');
  const { data: fitnessRows, isLoading: fitnessLoading } = useFitnessLeaderboard(selectedTestType);

  const fitnessMeta = FITNESS_TEST_META[selectedTestType];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Trophy className="w-7 h-7 text-yellow-500" />
          Leaderboards
        </h1>
        <p className="text-muted-foreground">Player rankings across match play and fitness tests</p>
      </div>

      {/* ── Match Play Rankings ── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            Match Play Rankings
          </CardTitle>
          <p className="text-sm text-muted-foreground">Ranked by net games won (games won − games conceded)</p>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-3 p-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <p className="text-muted-foreground text-center py-12 text-sm">
              No match results recorded yet. Add results in a Match Play session to see rankings.
            </p>
          ) : (
            <div className="divide-y divide-border">
              {rows.map((row, index) => {
                const rank = index + 1;
                const rankStyle = RANK_STYLES[rank] ?? 'bg-muted text-muted-foreground';
                const netSign = row.netGames > 0 ? '+' : '';
                const netColor =
                  row.netGames > 0
                    ? 'text-green-600 dark:text-green-400'
                    : row.netGames < 0
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-muted-foreground';

                return (
                  <div
                    key={row.playerId}
                    className="flex items-center gap-4 px-4 py-3"
                  >
                    <div
                      className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shrink-0',
                        rankStyle
                      )}
                    >
                      {rank}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{row.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {row.matches} match{row.matches !== 1 ? 'es' : ''}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className={cn('text-xl font-bold tabular-nums', netColor)}>
                        {netSign}{row.netGames}
                      </p>
                      <p className="text-xs text-muted-foreground tabular-nums">net games</p>
                    </div>
                    <div className="text-right shrink-0 w-16">
                      <p className="text-sm font-medium text-foreground tabular-nums">
                        {row.wins}W / {row.losses}L
                      </p>
                      <p className="text-xs text-muted-foreground tabular-nums">
                        {row.gamesWon}−{row.gamesConceded}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Fitness Rankings ── */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-500" />
              Fitness Rankings
            </CardTitle>
            <Select value={selectedTestType} onValueChange={setSelectedTestType}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STANDARD_TEST_TYPES.map((tt) => (
                  <SelectItem key={tt} value={tt}>
                    {FITNESS_TEST_META[tt].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {fitnessMeta && (
            <p className="text-sm text-muted-foreground">
              {fitnessMeta.label} · {fitnessMeta.unit} ·{' '}
              {fitnessMeta.higherIsBetter ? 'Higher is better' : 'Lower is better'}
            </p>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {fitnessLoading ? (
            <div className="space-y-3 p-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : fitnessRows.length === 0 ? (
            <p className="text-muted-foreground text-center py-12 text-sm">
              No results recorded for this test yet. Record fitness data in a Cardio or training session.
            </p>
          ) : (
            <div className="divide-y divide-border">
              {fitnessRows.map((row, index) => {
                const rank = index + 1;
                const rankStyle = RANK_STYLES[rank] ?? 'bg-muted text-muted-foreground';
                const formattedDate = new Date(row.date).toLocaleDateString('en-AU', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                });

                return (
                  <div
                    key={row.playerId}
                    className="flex items-center gap-4 px-4 py-3"
                  >
                    <div
                      className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shrink-0',
                        rankStyle
                      )}
                    >
                      {rank}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{row.name}</p>
                      <p className="text-xs text-muted-foreground">{formattedDate}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xl font-bold tabular-nums text-foreground">
                        {row.value}
                      </p>
                      <p className="text-xs text-muted-foreground tabular-nums">{row.unit}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
